import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';

import {
  FooterDocument,
  FooterQuery,
  FooterQueryVariables,
  FormDocument,
  FormQuery,
  FormQueryVariables,
  NavigationDocument,
  NavigationQuery,
  NavigationQueryVariables,
  ProductImageBlockFragment,
  SoftwareHeroDocument,
  SoftwareHeroQuery,
  SoftwareHeroQueryVariables,
  SoftwarePartnersBlogSectionDocument,
  SoftwarePartnersBlogSectionQuery,
  SoftwarePartnersBlogSectionQueryVariables,
  SoftwareProductImageBlockDocument,
  SoftwareProductImageBlockQuery,
  SoftwareProductImageBlockQueryVariables,
  SoftwareSeoDocument,
} from '@/api';
import { addApolloState, initializeApollo } from '@/api/apolloClient';
import { Layout } from '@/components/layout/Layout';
import { Partners } from '@/components/partners/Partners';
import { Seo } from '@/components/Seo';
import EnhancedProductImageBlock from '@/components/shared/EnhancedProductImageBlock';
import HeroVideoSection from '@/components/shared/HeroVideoSection';

export const getStaticProps: GetStaticProps = async () => {
  const client = initializeApollo();

  // prefetch
  await client.query({ query: SoftwareHeroDocument });
  await client.query({ query: SoftwareProductImageBlockDocument });
  await client.query({ query: SoftwarePartnersBlogSectionDocument });
  await client.query({ query: SoftwareSeoDocument });
  await client.query({ query: NavigationDocument });
  await client.query({ query: FooterDocument });
  await client.query({ query: FormDocument });

  const { data: seoData } = await client.query({
    query: SoftwareSeoDocument,
  });

  const { data: heroData } = await client.query<
    SoftwareHeroQuery,
    SoftwareHeroQueryVariables
  >({
    query: SoftwareHeroDocument,
  });

  const { data: productImageBlockData } = await client.query<
    SoftwareProductImageBlockQuery,
    SoftwareProductImageBlockQueryVariables
  >({
    query: SoftwareProductImageBlockDocument,
  });

  const { data: formData } = await client.query<FormQuery, FormQueryVariables>({
    query: FormDocument,
  });

  const { data: footerData } = await client.query<
    FooterQuery,
    FooterQueryVariables
  >({ query: FooterDocument });

  const { data: headerData } = await client.query<
    NavigationQuery,
    NavigationQueryVariables
  >({ query: NavigationDocument });

  const { data: partnersBlogSectionData } = await client.query<
    SoftwarePartnersBlogSectionQuery,
    SoftwarePartnersBlogSectionQueryVariables
  >({ query: SoftwarePartnersBlogSectionDocument });

  return addApolloState(client, {
    props: {
      seoData,
      heroData,
      productImageBlockData,
      partnersBlogSectionData,
      formData,
      footerData,
      headerData,
    },
  });
};

const Software: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  seoData,
  heroData,
  productImageBlockData,
  partnersBlogSectionData,
  formData,
  footerData,
  headerData,
}) => (
  <Layout formData={formData} footerData={footerData} headerData={headerData}>
    <Seo seo={seoData?.software?.data?.attributes?.seo} />
    <HeroVideoSection
      hero={heroData?.software?.data?.attributes?.heroVideoSection}
    />
    {productImageBlockData?.software?.data?.attributes?.productImageBlock?.map(
      (block: ProductImageBlockFragment | null, index: number) => (
        <EnhancedProductImageBlock
          productImageBlock={block}
          key={`enhancedProductImageBlock-software-item-${index}`}
          orientation={(index + 1) % 2 === 0 ? 'imageLeft' : 'imageRight'}
          parentIndex={index}
        />
      ),
    )}
    <Partners
      data={
        partnersBlogSectionData?.software?.data?.attributes?.partnersBlogSection
      }
    />
  </Layout>
);

export default Software;
